import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Badge,
  Chip,
  TextField,
  useTheme
} from '@mui/material';
import {
  LocalDining as DiningIcon,
  Restaurant as RestaurantIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  CreditCard as PaymentIcon,
  TableBar as TableIcon,
  LocalCafe as CafeIcon,
  LocalBar as BarIcon,
  Fastfood as FastfoodIcon
} from '@mui/icons-material';
import { POSProvider } from '../../contexts/POSContext';
import POSLayout from '../../layouts/POSLayout';

interface MenuItem {
  id: string;
  name: string;
  price: number;
  category: string;
  description?: string;
}

interface CartItem extends MenuItem {
  quantity: number;
  notes: string;
}

interface Table {
  id: string;
  number: number;
  status: 'available' | 'occupied' | 'reserved';
  seats: number;
}

const MENU_ITEMS: MenuItem[] = [
  { id: '1', name: 'Hamburger', price: 9.99, category: 'mains', description: 'Classic burger with lettuce, tomato, and cheese' },
  { id: '2', name: 'Cheeseburger', price: 10.99, category: 'mains', description: 'Burger with extra cheese' },
  { id: '3', name: 'Fish & Chips', price: 12.99, category: 'mains', description: 'Beer battered fish with fries' },
  { id: '4', name: 'Garden Salad', price: 7.99, category: 'mains', description: 'Fresh greens with house dressing' },
  { id: '5', name: 'French Fries', price: 3.99, category: 'sides', description: 'Crispy potato fries' },
  { id: '6', name: 'Onion Rings', price: 4.99, category: 'sides', description: 'Crispy battered onion rings' },
  { id: '7', name: 'Soft Drink', price: 2.49, category: 'drinks', description: 'Coke, Diet Coke, Sprite' },
  { id: '8', name: 'Beer', price: 5.99, category: 'drinks', description: 'Local craft beer' },
  { id: '9', name: 'Wine', price: 7.99, category: 'drinks', description: 'House red or white' },
  { id: '10', name: 'Coffee', price: 2.99, category: 'drinks', description: 'Freshly brewed' },
  { id: '11', name: 'Cheesecake', price: 6.99, category: 'desserts', description: 'New York style' },
  { id: '12', name: 'Ice Cream', price: 4.99, category: 'desserts', description: 'Vanilla, Chocolate, or Strawberry' },
];

const TABLES: Table[] = [
  { id: '1', number: 1, status: 'available', seats: 2 },
  { id: '2', number: 2, status: 'occupied', seats: 2 },
  { id: '3', number: 3, status: 'available', seats: 4 },
  { id: '4', number: 4, status: 'reserved', seats: 4 },
  { id: '5', number: 5, status: 'occupied', seats: 6 },
  { id: '6', number: 6, status: 'available', seats: 6 },
  { id: '7', number: 7, status: 'available', seats: 8 },
  { id: '8', number: 8, status: 'available', seats: 2 },
];

const RestaurantPOSContent: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<string>('mains');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [orderNotes, setOrderNotes] = useState('');
  
  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };
  
  const addToCart = (item: MenuItem) => {
    const existingItemIndex = cart.findIndex(cartItem => cartItem.id === item.id);
    
    if (existingItemIndex >= 0) {
      // Item already in cart, increase quantity
      const updatedCart = [...cart];
      updatedCart[existingItemIndex].quantity += 1;
      setCart(updatedCart);
    } else {
      // Add new item to cart
      setCart([...cart, { ...item, quantity: 1, notes: '' }]);
    }
  };
  
  const removeFromCart = (id: string) => {
    const existingItemIndex = cart.findIndex(item => item.id === id);
    
    if (existingItemIndex >= 0) {
      const updatedCart = [...cart];
      if (updatedCart[existingItemIndex].quantity > 1) {
        // Decrease quantity
        updatedCart[existingItemIndex].quantity -= 1;
      } else {
        // Remove item completely
        updatedCart.splice(existingItemIndex, 1);
      }
      setCart(updatedCart);
    }
  };
  
  const deleteFromCart = (id: string) => {
    setCart(cart.filter(item => item.id !== id));
  };
  
  const updateItemNotes = (id: string, notes: string) => {
    setCart(cart.map(item => 
      item.id === id ? { ...item, notes } : item
    ));
  };
  
  const selectTable = (table: Table) => {
    setSelectedTable(table);
  };
  
  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };
  
  const handlePlaceOrder = () => {
    if (!selectedTable) {
      alert('Please select a table first');
      return;
    }
    
    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }
    
    alert(`Order placed for Table ${selectedTable.number}!`);
    // In a real app, this would send the order to the kitchen and update the database
    setCart([]);
    setOrderNotes('');
  };
  
  const filteredMenuItems = MENU_ITEMS.filter(item => item.category === activeTab);
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" gutterBottom>
        Restaurant POS
      </Typography>
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        {/* Left side: Menu and Tables */}
        <Grid xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Tabs
              value={activeTab}
              onChange={handleCategoryChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab 
                icon={<RestaurantIcon />} 
                label="Mains" 
                value="mains" 
                iconPosition="start" 
              />
              <Tab 
                icon={<FastfoodIcon />} 
                label="Sides" 
                value="sides" 
                iconPosition="start" 
              />
              <Tab 
                icon={<BarIcon />} 
                label="Drinks" 
                value="drinks" 
                iconPosition="start" 
              />
              <Tab 
                icon={<CafeIcon />} 
                label="Desserts" 
                value="desserts" 
                iconPosition="start" 
              />
            </Tabs>
            
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {filteredMenuItems.map((item) => (
                  <Grid
                    key={item.id}
                    xs={12}
                    sm={6}
                    md={4}>
                    <Card sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {item.description}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          ${item.price.toFixed(2)}
                        </Typography>
                      </CardContent>
                      <Box sx={{ p: 1 }}>
                        <Button 
                          fullWidth 
                          variant="contained" 
                          startIcon={<AddIcon />}
                          onClick={() => addToCart(item)}
                        >
                          Add
                        </Button>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
          
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              <TableIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Tables
            </Typography>
            <Grid container spacing={2}>
              {TABLES.map((table) => (
                <Grid
                  key={table.id}
                  xs={6}
                  sm={4}
                  md={3}>
                  <Card 
                    sx={{ 
                      textAlign: 'center',
                      p: 2,
                      cursor: 'pointer',
                      backgroundColor: 
                        selectedTable?.id === table.id 
                          ? theme.palette.primary.light
                          : table.status === 'occupied' 
                            ? theme.palette.error.light 
                            : table.status === 'reserved' 
                              ? theme.palette.warning.light
                              : theme.palette.success.light,
                      color: selectedTable?.id === table.id ? 'white' : 'inherit',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                    onClick={() => table.status !== 'occupied' && selectTable(table)}
                  >
                    <Typography variant="h6">
                      Table {table.number}
                    </Typography>
                    <Typography variant="body2">
                      {table.seats} seats
                    </Typography>
                    <Chip 
                      label={table.status} 
                      size="small" 
                      sx={{ mt: 1 }}
                      color={
                        table.status === 'available' 
                          ? 'success' 
                          : table.status === 'occupied' 
                            ? 'error' 
                            : 'warning'
                      }
                    />
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        
        {/* Right side: Cart and Order */}
        <Grid
          xs={12}
          md={4}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                <CartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Current Order
              </Typography>
              {selectedTable && (
                <Chip 
                  label={`Table ${selectedTable.number}`} 
                  color="primary" 
                  variant="outlined" 
                />
              )}
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            {cart.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ my: 4, textAlign: 'center' }}>
                No items in cart
              </Typography>
            ) : (
              <List sx={{ overflow: 'auto', flexGrow: 1 }}>
                {cart.map((item) => (
                  <React.Fragment key={item.id}>
                    <ListItem>
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        <IconButton size="small" onClick={() => removeFromCart(item.id)}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body1" sx={{ mx: 1 }}>
                          {item.quantity}
                        </Typography>
                        <IconButton size="small" onClick={() => addToCart(item)}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <ListItemText 
                        primary={item.name} 
                        secondary={
                          <TextField
                            placeholder="Add notes..."
                            variant="standard"
                            size="small"
                            fullWidth
                            value={item.notes}
                            onChange={(e) => updateItemNotes(item.id, e.target.value)}
                            sx={{ mt: 1 }}
                          />
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography variant="body1">
                            ${(item.price * item.quantity).toFixed(2)}
                          </Typography>
                          <IconButton edge="end" size="small" onClick={() => deleteFromCart(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
            
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Order Notes"
                multiline
                rows={2}
                fullWidth
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">${calculateTotal().toFixed(2)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Tax (7.25%):</Typography>
                <Typography variant="body1">${(calculateTotal() * 0.0725).toFixed(2)}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6">${(calculateTotal() * 1.0725).toFixed(2)}</Typography>
              </Box>
              
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<PaymentIcon />}
                onClick={handlePlaceOrder}
                disabled={cart.length === 0 || !selectedTable}
              >
                Place Order
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

const RestaurantPOS: React.FC = () => {
  return (
    <POSProvider>
      <POSLayout>
        <RestaurantPOSContent />
      </POSLayout>
    </POSProvider>
  );
};

export default RestaurantPOS; 